import { useNavigate } from 'react-router-dom'
import { notifications } from '@mantine/notifications'

import {
  useCreateOfferMutation,
  useUpdateOfferMutation,
} from 'src/Redux/api/offersApi'
import { useGetProfileQuery } from 'src/Redux/api/authApi'

import { ModelCalculated } from './useCalculator'
import {
  formatDateToYYYYMMDD,
  getUnitQuantity,
  getUnitsQuantity,
  OfferFormData,
} from './helpers'

/**
 * Хук для операций с коммерческими предложениями
 * Обеспечивает создание новых предложений и обновление существующих
 */

interface UseOfferOperationsProps {
  offerId?: string
  isLoading?: boolean
  setInitialValues?: (
    formData: OfferFormData,
    itemsData: ModelCalculated[],
  ) => void
}

export const useOfferOperations = ({
  offerId,
  setInitialValues,
}: UseOfferOperationsProps): {
  saveOffer: (
    formData: OfferFormData,
    calculatedEquipments: ModelCalculated[],
  ) => Promise<boolean>
  isLoading: boolean
} => {
  const navigate = useNavigate()
  const { data: user } = useGetProfileQuery()
  const [createOffer, { isLoading: isCreateOfferLoading }] =
    useCreateOfferMutation()
  const [updateOffer, { isLoading: isUpdateOfferLoading }] =
    useUpdateOfferMutation()

  const saveOffer = async (
    formData: OfferFormData,
    calculatedEquipments: ModelCalculated[],
  ): Promise<boolean> => {
    const offer = {
      clientId: Number(formData?.clientId),
      userId: Number(user?.id),
      clientContactName: formData?.clientContactName,
      clientContactNamePosition: formData?.clientContactNamePosition,
      clientDivision: formData?.clientDivision,
      fuelPrice: Number(formData?.fuelPrice),
      deliveryPrice: Number(formData?.partDeliveryPrice),
      personDeliveryPrice: Number(formData?.personDeliveryPrice),
      accommodationPrice: Number(formData?.accommodationPrice),
      foodPrice: Number(formData?.foodPrice),
      personnelHouseholdServicesCost: formData?.personnelHouseholdServicesCost
        ? Number(formData?.personnelHouseholdServicesCost)
        : 0,
      dailyMedCheckCost: Number(formData?.dailyMedCheckCost),
      minRentPeriod: Number(formData?.minRentPeriod),
      personalBenefits: formData?.personalBenefits,
      vatVisibility: formData?.vatVisibility,
      createDate: formatDateToYYYYMMDD(formData?.createDate),
      validityDaysCount: Number(formData?.validityDaysCount),
      season: formData?.season,
      models: calculatedEquipments.map((item) => ({
        id: item.id,
        specialPrice:
          item.specialPrice != null ? Number(item.specialPrice) : undefined,
        totalRentPricePerHour: Number(item.totalRentPricePerHour),
        count: getUnitsQuantity(item.units),
        shiftsCount:
          item.shiftsCount != null ? Number(item.shiftsCount) : undefined,
        route: item.route,
        relocationCost:
          item.relocationCost != null ? Number(item.relocationCost) : undefined,
        toolId:
          item.selectedTool != null ? Number(item.selectedTool) : undefined,
        isDetalized: Boolean(item.detalization),
        selectedImageUrl: item.selectedImageUrl,
        units: (item.units || []).map((unit) => ({
          departurePoint: unit.departurePoint,
          workPoint: unit.workPoint,
          returnPoint: unit.returnPoint,
          deliveryCost:
            unit.deliveryCost != null ? Number(unit.deliveryCost) : undefined,
          returnCost:
            unit.returnCost != null ? Number(unit.returnCost) : undefined,
          quantity: getUnitQuantity(unit),
          shiftCount:
            unit.shiftCount != null ? Number(unit.shiftCount) : undefined,
          toolId: unit.toolId != null ? Number(unit.toolId) : undefined,
          totalRentPricePerHour:
            unit.totalRentPricePerHour != null
              ? Number(unit.totalRentPricePerHour)
              : undefined,
        })),
      })),
    }

    try {
      if (offerId) {
        await updateOffer({ id: Number(offerId), data: offer })
          .unwrap()
          .then(() => {
            notifications.show({
              title: 'КП успешно изменено',
              message: '',
              position: 'top-right',
            })

            // Обновляем начальные значения после успешного сохранения
            if (setInitialValues) {
              const formDataWithCost = {
                ...formData,
                personnelHouseholdServicesCost:
                  Number(formData.personnelHouseholdServicesCost) || 0,
              }
              setInitialValues(formDataWithCost, calculatedEquipments)
            }
          })
      } else {
        await createOffer(offer)
          .unwrap()
          .then((result) => {
            notifications.show({
              title: 'КП успешно сохранено',
              message: '',
              position: 'top-right',
            })

            navigate(`/offers/${result.id}`)
          })
      }
      return true
    } catch {
      return false
    }
  }

  return {
    saveOffer,
    isLoading: isCreateOfferLoading || isUpdateOfferLoading,
  }
}
